/* eslint-disable tailwindcss/no-arbitrary-value */
import { HTMLProps, PropsWithChildren, ReactElement } from 'react';
import { cn } from '@ui/lib';
import { Icon, IconProps } from '../../elements/icon/Icon';
import { Body1, Title2, Title3, Title4, Title5 } from '../../tokens';

interface TldrVariantIllustratedProps {
  decorated: 'illustrated';
  title: string;
  icon: IconProps;
}

interface TldrVariantBaseProps {
  decorated?: 'base';
  title: string;
  variant: 'tldr';
}

type TldrSize = 'xs' | 'sm' | 'md' | 'lg';

interface TldrCommonProps {
  variant: 'tldr';
  title: string;
  size?: TldrSize;
}

export type TldrVariantProps = (
  | TldrVariantIllustratedProps
  | TldrVariantBaseProps
) &
  TldrCommonProps;

export type DetailVariantProps = {
  description?: string;
  icon?: IconProps;
  title?: string;
  subtitle?: string;
  decoration?: 'base' | 'illustrated' | 'icon' | 'avatar';
  variant: 'detail';
  size?: 'sm' | 'md' | 'lg' | 'xs';
  className?: string;
};

// @TODO: Implement this variant
type DescriptionVariantProps = {
  variant: 'description';
};

type WithHtmlDivProps<T> = T & Omit<HTMLProps<HTMLDivElement>, 'size'>;
export type ContentProps =
  | TldrVariantProps
  | DetailVariantProps
  | (DescriptionVariantProps & { className?: string });

const DetailVariant = ({
  icon,
  title = '',
  subtitle = '',
  size = 'lg',
  ...rest
}: Omit<WithHtmlDivProps<DetailVariantProps>, 'variant'>) => {
  const { className, ...props } = rest;

  return (
    <div
      className={cn(
        `inline-flex w-full items-start justify-start gap-md`,
        className,
      )}
      {...props}
    >
      {icon && (
        <div
          className={cn('flex items-center justify-center', {
            'size-lg': size === 'lg',
            'size-md': size === 'md',
            'size-sm': size === 'sm',
            'size-xs': size === 'xs',
          })}
        >
          <Icon className="text-negative-strong" {...icon} />
        </div>
      )}
      <div className="inline-flex shrink grow basis-0 flex-col items-start justify-start gap-0.5 whitespace-normal">
        <Title4>{title}</Title4>
        <Body1 className="text-on-screen-surface-subdued">{subtitle}</Body1>
      </div>
    </div>
  );
};

const TldrVariant = ({
  title,
  className,
  size = 'md',
  ...rest
}: WithHtmlDivProps<TldrVariantProps>) => {
  const TldrSizeComponentMap = {
    xs: Title5,
    sm: Title4,
    md: Title3,
    lg: Title2,
  };
  const TitleComponent = TldrSizeComponentMap[size];

  const decorated = rest.decorated ?? '';

  return (
    <div
      className={cn(
        'inline-flex h-6 items-start justify-start gap-4',
        decorated === 'base' ? 'items-center' : '',
        className,
      )}
    >
      {'icon' in rest && <Icon {...rest.icon} />}
      <TitleComponent>{title}</TitleComponent>
    </div>
  );
};

export const Content = ({ variant = 'detail', ...rest }: ContentProps) => {
  switch (variant) {
    case 'detail':
      return <DetailVariant {...(rest as DetailVariantProps)} />;
    case 'tldr':
      return <TldrVariant {...(rest as TldrVariantProps)} />;
    default:
      throw new Error(`Variant ${variant} is not implemented!`);
  }
};
